import React, { useState, useEffect } from "react";
import {
  BookOpen,
  Music,
  Heart,
  Camera,
  PenTool,
  Sparkles,
  Lightbulb,
  Download,
  Share2,
  Star,
  ChevronRight,
  Play,
  Home
} from "lucide-react";

// Tips of the day - rotating daily inspiration
const TIPS_OF_THE_DAY = [
  { tip: "Try pairing a song with your journal entry — it creates a beautiful memory capsule!", emoji: "🎵", feature: "Journal" },
  { tip: "Use 'Through Their Eyes' to write a story from your pet's perspective. It's surprisingly moving!", emoji: "👀", feature: "Create" },
  { tip: "The AI Story Starter can spark your creativity when you're stuck. Just enter your pet's name and click 'Give me an idea!'", emoji: "✨", feature: "Create" },
  { tip: "Story Seeds pulls inspiration from your own memories. The best stories come from real moments!", emoji: "💡", feature: "Create" },
  { tip: "Download your stories as PDF mini-books — perfect for framing or gifting!", emoji: "📖", feature: "Create" },
  { tip: "The Song of the Day changes each morning. Start your day with a new melody!", emoji: "🌅", feature: "Home" },
  { tip: "Share specific songs with friends using the 'Share Song' button — they'll land right on that song!", emoji: "🔗", feature: "Songbook" },
  { tip: "Guided journal prompts can help when you don't know what to write. Look for the lantern icon!", emoji: "🏮", feature: "Journal" },
  { tip: "Your journaling streak celebrates consistency. Even a few words count!", emoji: "🔥", feature: "Journal" },
  { tip: "The Comfort Corner has breathing exercises and calming activities for tough days.", emoji: "🧘", feature: "Comfort" },
  { tip: "Filter songs by mood to find exactly what you need right now.", emoji: "💭", feature: "Songbook" },
  { tip: "Rainbow Bridge is a gentle space to honor pets who've crossed over. Take your time there.", emoji: "🌈", feature: "Create" },
  { tip: "Letter to My Pet lets you write what's in your heart. Your pet would love to hear it.", emoji: "💌", feature: "Create" },
  { tip: "Add photos to your pet profiles — they'll appear throughout the app!", emoji: "📸", feature: "Pets" },
  { tip: "The daily quote changes each day. Some days, it's exactly what you needed to hear.", emoji: "💬", feature: "Home" },
];

// Get today's tip based on date
const getTodaysTip = () => {
  const today = new Date();
  const dayOfYear = Math.floor((today - new Date(today.getFullYear(), 0, 0)) / (1000 * 60 * 60 * 24));
  return TIPS_OF_THE_DAY[dayOfYear % TIPS_OF_THE_DAY.length];
};

export const HowToUsePage = ({ setCurrentPage }) => {
  const [todaysTip] = useState(getTodaysTip());
  
  const features = [
    {
      id: "home",
      title: "Home",
      icon: Home,
      color: "bg-amber-500",
      description: "Your peaceful landing spot",
      details: [
        "Daily inspirational quote that changes each morning",
        "Song of the Day — a featured song to start your day",
        "Quick stats showing your pets, songs, moments, and journal entries",
        "Quick action buttons to jump to common tasks"
      ]
    },
    {
      id: "pets",
      title: "Pets",
      icon: Heart,
      color: "bg-rose-500",
      description: "Profiles for your beloved companions",
      details: [
        "Create detailed profiles with photos, species, breed, and personality",
        "Add favorite activities, toys, and special quirks",
        "Your pets appear in stories and journal entries",
        "Celebrate each unique personality"
      ]
    },
    {
      id: "songbook",
      title: "Songbook",
      icon: Music,
      color: "bg-sage",
      description: "A curated collection of comforting music",
      details: [
        "Browse 100+ songs for pets and their people",
        "Filter by mood: Peaceful, Happy, Loving, and more",
        "Search by title, artist, or keywords",
        "Share specific songs with friends via direct links",
        "Storybook view shows lyrics and illustrations"
      ]
    },
    {
      id: "create",
      title: "Create (Storybook)",
      icon: PenTool,
      color: "bg-sky-500",
      description: "Write stories about your pets",
      details: [
        "7 templates: Through Their Eyes, Letter to My Pet, Rainbow Bridge, Adventure, Mystery, Cozy Day, Blank Canvas",
        "AI Story Starter generates creative opening lines",
        "Story Seeds pulls inspiration from your own memories",
        "Add illustrations and pair with songs",
        "Download as PDF mini-books for printing or gifting"
      ]
    },
    {
      id: "moments",
      title: "Moments",
      icon: Camera,
      color: "bg-purple-500",
      description: "Photo memories of special times",
      details: [
        "Upload photos with captions",
        "Tag moments with pets and categories",
        "Build a visual timeline of memories",
        "These become Story Seeds for your writing"
      ]
    },
    {
      id: "journal",
      title: "Journal",
      icon: BookOpen,
      color: "bg-terracotta",
      description: "Reflect, remember, and process",
      details: [
        "Mood check-ins with gentle prompts",
        "Gratitude entries to celebrate the good",
        "Story writing for longer reflections",
        "Guided prompts when you don't know what to write (look for 🏮)",
        "Pair entries with songs for a complete memory",
        "Track your journaling streak"
      ]
    },
  ];

  return (
    <div data-testid="how-to-use-page" className="fade-in">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="font-heading text-4xl font-bold text-charcoal mb-2">How to Use Paws & Harmony 📖</h1>
        <p className="text-stone max-w-xl mx-auto">
          A gentle guide to discovering all the ways this space can bring you peace, creativity, and connection.
        </p>
      </div>
      
      {/* Tip of the Day */}
      <div className="mb-8 bg-gradient-to-r from-amber-50 via-orange-50 to-rose-50 rounded-3xl p-6 border border-amber-200">
        <div className="flex items-start gap-4">
          <div className="text-4xl">{todaysTip.emoji}</div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <Lightbulb className="text-amber-500" size={20} />
              <h2 className="font-heading text-lg font-semibold text-amber-800">Tip of the Day</h2>
              <span className="text-xs bg-amber-200 text-amber-700 px-2 py-0.5 rounded-full">{todaysTip.feature}</span>
            </div>
            <p className="text-charcoal">{todaysTip.tip}</p>
          </div>
        </div>
      </div>
      
      {/* Quick Start */}
      <div className="mb-8 bg-white/90 backdrop-blur-sm rounded-3xl p-6 shadow-sm border border-white/50">
        <h2 className="font-heading text-2xl font-semibold text-charcoal mb-4 flex items-center gap-2">
          <Sparkles className="text-sage" size={24} />
          5 Things to Try First
        </h2>
        <div className="grid md:grid-cols-5 gap-4">
          {[
            { num: 1, text: "Add your first pet", page: "pets", emoji: "🐾" },
            { num: 2, text: "Browse the Songbook", page: "songbook", emoji: "🎵" },
            { num: 3, text: "Write a journal entry", page: "journal", emoji: "📝" },
            { num: 4, text: "Create a story", page: "storybook", emoji: "✨" },
            { num: 5, text: "Download a PDF", page: "storybook", emoji: "📖" },
          ].map((item) => (
            <button
              key={item.num}
              onClick={() => setCurrentPage(item.page)}
              className="p-4 bg-gradient-to-br from-sage/10 to-terracotta/10 rounded-2xl text-center hover:shadow-md transition-all hover:-translate-y-1"
            >
              <div className="text-3xl mb-2">{item.emoji}</div>
              <div className="text-xs text-sage font-bold mb-1">Step {item.num}</div>
              <p className="text-sm text-charcoal font-medium">{item.text}</p>
            </button>
          ))}
        </div>
      </div>
      
      {/* Feature Guide */}
      <div className="space-y-4">
        <h2 className="font-heading text-2xl font-semibold text-charcoal mb-4">Feature Guide</h2>
        
        {features.map((feature) => (
          <div key={feature.id} className="bg-white/90 backdrop-blur-sm rounded-2xl overflow-hidden shadow-sm border border-white/50">
            <button
              onClick={() => setCurrentPage(feature.id)}
              className="w-full p-4 flex items-center gap-4 hover:bg-gray-50 transition-all text-left"
            >
              <div className={`w-12 h-12 ${feature.color} rounded-xl flex items-center justify-center text-white`}>
                <feature.icon size={24} />
              </div>
              <div className="flex-1">
                <h3 className="font-heading text-lg font-semibold text-charcoal">{feature.title}</h3>
                <p className="text-sm text-stone">{feature.description}</p>
              </div>
              <ChevronRight className="text-gray-400" size={20} />
            </button>
            <div className="px-4 pb-4 pt-0">
              <ul className="ml-16 space-y-1">
                {feature.details.map((detail, i) => (
                  <li key={i} className="text-sm text-stone flex items-start gap-2">
                    <span className="text-sage mt-1">•</span>
                    {detail}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>
      
      {/* Pro Tips */}
      <div className="mt-8 bg-gradient-to-r from-purple-50 to-pink-50 rounded-3xl p-6 border border-purple-200">
        <h2 className="font-heading text-xl font-semibold text-purple-800 mb-4 flex items-center gap-2">
          <Star className="text-purple-500" size={20} />
          Pro Tips
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="flex items-start gap-3">
            <span className="text-2xl">🎧</span>
            <div>
              <p className="font-medium text-charcoal">Song + Journal = Magic</p>
              <p className="text-sm text-stone">Pair a song with your journal entry. When you revisit it, the music brings the memory flooding back.</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <span className="text-2xl">👀</span>
            <div>
              <p className="font-medium text-charcoal">Write as Your Pet</p>
              <p className="text-sm text-stone">"Through Their Eyes" stories are surprisingly emotional. Try writing as your pet looking at you.</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <span className="text-2xl">🌈</span>
            <div>
              <p className="font-medium text-charcoal">Rainbow Bridge is Healing</p>
              <p className="text-sm text-stone">If you've lost a pet, the Rainbow Bridge template provides gentle prompts for honoring them.</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <span className="text-2xl">📖</span>
            <div>
              <p className="font-medium text-charcoal">Gift a Mini-Book</p>
              <p className="text-sm text-stone">Download your story as a PDF and print it. It makes a beautiful, personal gift.</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Footer */}
      <div className="mt-8 text-center text-stone">
        <p className="font-handwritten text-xl text-sage">
          Take your time. There's no rush here. 💕
        </p>
        <p className="text-sm mt-2">
          Questions? The Comfort Corner has more resources, or just explore at your own pace.
        </p>
      </div>
    </div>
  );
};

export default HowToUsePage;
